import { gql } from '@urql/core';

export const GET_STOCK_LOCATIONS = gql`
  query GetStockLocations($options: StockLocationListOptions) {
    stockLocations(options: $options) {
      items {
        id
        name
        description
        customFields {
          locationType
          status
          isMainLocation
          fulfillDelivery
          fulfillPickup
          priorityWeight
          addressLine1
          city
          province
          postalCode
          phone
          latitude
          longitude
        }
      }
      totalItems
    }
  }
`;

export const GET_PRODUCT_VARIANT_STOCK = gql`
  query GetProductVariantStock($id: ID!) {
    productVariant(id: $id) {
      id
      name
      sku
      stockOnHand
      stockAllocated
      stockLevels {
        stockLocationId
        stockOnHand
        stockAllocated
      }
    }
  }
`;

export const UPDATE_PRODUCT_VARIANT_STOCK = gql`
  mutation UpdateProductVariants($input: [UpdateProductVariantInput!]!) {
    updateProductVariants(input: $input) {
      id
      sku
      stockOnHand
      stockLevels {
        stockLocationId
        stockOnHand
        stockAllocated
      }
    }
  }
`;

export const MANUAL_ADJUST_INVENTORY = gql`
  mutation ManualAdjustInventory($input: [ManualInventoryAdjustmentInput!]!) {
    manualAdjustInventory(input: $input) {
      success
      adjustedCount
      warnings {
        productVariantId
        stockLocationId
        stockOnHand
        stockAllocated
        code
      }
    }
  }
`;

export const GET_INVENTORY_VARIANTS = gql`
  query GetInventoryVariants($options: InventoryListOptions) {
    inventoryVariants(options: $options) {
      items {
        id
        name
        sku
        enabled
        stockOnHand
        onHand
        stockAllocated
        reserved
        available
        incoming
        inTransit
        quarantine
        lots {
          lotNumber
          expiryDate
          quantity
          daysToExpiry
        }
        trackInventory
        outOfStockThreshold
        useGlobalOutOfStockThreshold
        stockLevels {
          id
          updatedAt
          stockLocationId
          stockOnHand
          stockAllocated
        }
        options {
          id
          name
          code
        }
        product {
          id
          name
          brandId
          featuredAsset {
            preview
          }
        }
      }
      totalItems
      counts {
        all
        lowStock
        outOfStock
      }
    }
  }
`;

export const GET_STOCK_LOCATION = gql`
  query GetStockLocation($id: ID!) {
    stockLocation(id: $id) {
      id
      name
      description
      customFields {
        locationType
        status
        isMainLocation
        fulfillDelivery
        fulfillPickup
        priorityWeight
        addressLine1
        city
        province
        postalCode
        phone
        latitude
        longitude
        serviceCities
        workingHours
        pickupInstructions
        pickupPrepareTimeMinutes
        pickupCodEnabled
      }
    }
  }
`;

export const SYNC_PICKUP_RATE = gql`
  mutation SyncPickupRate {
    syncPickupRate {
      id
      code
      deliveryType
      isEnabled
    }
  }
`;

export const CREATE_STOCK_LOCATION = gql`
  mutation CreateStockLocation($input: CreateStockLocationInput!) {
    createStockLocation(input: $input) {
      id
      name
      description
      customFields {
        locationType
        status
        isMainLocation
      }
    }
  }
`;

export const UPDATE_STOCK_LOCATION = gql`
  mutation UpdateStockLocation($input: UpdateStockLocationInput!) {
    updateStockLocation(input: $input) {
      id
      name
      description
      customFields {
        locationType
        status
        isMainLocation
      }
    }
  }
`;

export const REORDER_STOCK_LOCATIONS = gql`
  mutation ReorderStockLocations($locationIds: [ID!]!) {
    reorderStockLocations(locationIds: $locationIds) {
      id
      customFields {
        priorityWeight
      }
    }
  }
`;

export const DELETE_STOCK_LOCATION = gql`
  mutation DeleteStockLocation($input: DeleteStockLocationInput!) {
    deleteStockLocation(input: $input) {
      result
      message
    }
  }
`;

export const GET_STAFF_STOCK_LOCATION_ASSIGNMENTS = gql`
  query GetStaffStockLocationAssignments($administratorId: ID!) {
    staffStockLocationAssignments(administratorId: $administratorId) {
      id
      name
      description
    }
  }
`;

export const SET_STAFF_STOCK_LOCATIONS = gql`
  mutation SetStaffStockLocations($input: SetStaffStockLocationsInput!) {
    setStaffStockLocations(input: $input) {
      success
      message
    }
  }
`;

export const GET_MY_STOCK_LOCATION_ASSIGNMENTS = gql`
  query GetMyStockLocationAssignments {
    myStockLocationAssignments
  }
`;

export const GET_TRANSFERS = gql`
  query GetTransfers($options: TransferListOptions) {
    transfers(options: $options) {
      items {
        id
        createdAt
        updatedAt
        transferNumber
        state
        sourceLocationId
        destinationLocationId
        notes
        shippedAt
        receivedAt
        items {
          id
          productVariantId
          quantity
          receivedQuantity
        }
      }
      totalItems
    }
  }
`;

export const GET_TRANSFER = gql`
  query GetTransfer($id: ID!) {
    transfer(id: $id) {
      id
      createdAt
      updatedAt
      transferNumber
      state
      sourceLocationId
      destinationLocationId
      notes
      shippedAt
      receivedAt
      items {
        id
        productVariantId
        quantity
        receivedQuantity
      }
    }
  }
`;

export const CREATE_TRANSFER = gql`
  mutation CreateTransfer($input: CreateTransferInput!) {
    createTransfer(input: $input) {
      id
      transferNumber
      state
    }
  }
`;

export const UPDATE_TRANSFER = gql`
  mutation UpdateTransfer($input: UpdateTransferInput!) {
    updateTransfer(input: $input) {
      id
      transferNumber
      state
    }
  }
`;

export const TRANSITION_TRANSFER = gql`
  mutation TransitionTransfer($id: ID!, $state: TransferState!) {
    transitionTransfer(id: $id, state: $state) {
      id
      transferNumber
      state
      shippedAt
      receivedAt
    }
  }
`;

export const RECEIVE_TRANSFER_ITEMS = gql`
  mutation ReceiveTransferItems($id: ID!, $items: [ReceiveTransferItemInput!]!) {
    receiveTransferItems(id: $id, items: $items) {
      id
      transferNumber
      state
      items {
        id
        productVariantId
        quantity
        receivedQuantity
      }
    }
  }
`;

export const DELETE_TRANSFER = gql`
  mutation DeleteTransfer($id: ID!) {
    deleteTransfer(id: $id) {
      result
      message
    }
  }
`;

export const GET_VENDORS = gql`
  query GetVendors($options: VendorListOptions) {
    vendors(options: $options) {
      items {
        id
        createdAt
        updatedAt
        name
        email
        phone
        company
        notes
      }
      totalItems
    }
  }
`;

export const GET_VENDOR = gql`
  query GetVendor($id: ID!) {
    vendor(id: $id) {
      id
      createdAt
      updatedAt
      name
      email
      phone
      company
      notes
    }
  }
`;

export const CREATE_VENDOR = gql`
  mutation CreateVendor($input: CreateVendorInput!) {
    createVendor(input: $input) {
      id
      name
    }
  }
`;

export const UPDATE_VENDOR = gql`
  mutation UpdateVendor($input: UpdateVendorInput!) {
    updateVendor(input: $input) {
      id
      name
    }
  }
`;

export const DELETE_VENDOR = gql`
  mutation DeleteVendor($id: ID!) {
    deleteVendor(id: $id) {
      result
      message
    }
  }
`;

export const GET_PURCHASE_ORDERS = gql`
  query GetPurchaseOrders($options: PurchaseOrderListOptions) {
    purchaseOrders(options: $options) {
      items {
        id
        createdAt
        updatedAt
        orderNumber
        state
        vendorId
        destinationLocationId
        notes
        referenceNumber
        expectedAt
        sentAt
        receivedAt
        totalCost
        items {
          id
          productVariantId
          quantity
          receivedQuantity
          unitCost
        }
      }
      totalItems
    }
  }
`;

export const GET_PURCHASE_ORDER = gql`
  query GetPurchaseOrder($id: ID!) {
    purchaseOrder(id: $id) {
      id
      createdAt
      updatedAt
      orderNumber
      state
      vendorId
      destinationLocationId
      notes
      referenceNumber
      expectedAt
      sentAt
      receivedAt
      totalCost
      items {
        id
        productVariantId
        quantity
        receivedQuantity
        unitCost
      }
    }
  }
`;

export const CREATE_PURCHASE_ORDER = gql`
  mutation CreatePurchaseOrder($input: CreatePurchaseOrderInput!) {
    createPurchaseOrder(input: $input) {
      id
      orderNumber
      state
    }
  }
`;

export const UPDATE_PURCHASE_ORDER = gql`
  mutation UpdatePurchaseOrder($input: UpdatePurchaseOrderInput!) {
    updatePurchaseOrder(input: $input) {
      id
      orderNumber
      state
    }
  }
`;

export const TRANSITION_PURCHASE_ORDER = gql`
  mutation TransitionPurchaseOrder($id: ID!, $state: PurchaseOrderState!) {
    transitionPurchaseOrder(id: $id, state: $state) {
      id
      orderNumber
      state
      sentAt
      receivedAt
    }
  }
`;

export const RECEIVE_PURCHASE_ORDER_ITEMS = gql`
  mutation ReceivePurchaseOrderItems($id: ID!, $items: [ReceivePurchaseOrderItemInput!]!) {
    receivePurchaseOrderItems(id: $id, items: $items) {
      id
      orderNumber
      state
      items {
        id
        productVariantId
        quantity
        receivedQuantity
        unitCost
      }
    }
  }
`;

export const DELETE_PURCHASE_ORDER = gql`
  mutation DeletePurchaseOrder($id: ID!) {
    deletePurchaseOrder(id: $id) {
      result
      message
    }
  }
`;

export const GET_LOW_STOCK_THRESHOLD = gql`
  query GetLowStockThreshold {
    activeChannel {
      id
      outOfStockThreshold
      customFields {
        lowStockThreshold
      }
    }
    globalSettings {
      id
      trackInventory
    }
  }
`;

export const GET_INVENTORY_SETTINGS = gql`
  query GetInventorySettings {
    activeChannel {
      id
      customFields {
        fulfillmentProfile
        allocationStrategy
        splitPolicy
      }
    }
  }
`;

export const UPDATE_INVENTORY_SETTINGS = gql`
  mutation UpdateInventorySettings($input: UpdateInventoryRoutingSettingsInput!) {
    updateInventoryRoutingSettings(input: $input) {
      success
      errorCode
      errorDetails
      settings {
        fulfillmentProfile
        allocationStrategy
        splitPolicy
      }
    }
  }
`;

export const GET_STOCK_LOCATION_CARRIERS = gql`
  query GetStockLocationCarriers($stockLocationId: ID!) {
    stockLocationCarriers(stockLocationId: $stockLocationId) {
      id
      stockLocationId
      carrierCredentialsId
      carrierCredentials {
        id
        carrierCode
        isEnabled
        mode
        codEnabled
      }
      isEnabled
      sortOrder
    }
  }
`;

export const GET_STOCK_LOCATION_SERVICE_AREAS = gql`
  query GetStockLocationServiceAreas($stockLocationId: ID!) {
    stockLocationServiceAreas(stockLocationId: $stockLocationId) {
      id
      stockLocationId
      platformCityId
      radiusKm
      prepTimeMinutes
      deliverySlaMinutes
      isEnabled
      platformCity {
        id
        nameAr
        nameEn
        latitude
        longitude
      }
    }
  }
`;

export const SET_STOCK_LOCATION_SERVICE_AREAS = gql`
  mutation SetStockLocationServiceAreas($stockLocationId: ID!, $areas: [StockLocationServiceAreaInput!]!) {
    setStockLocationServiceAreas(stockLocationId: $stockLocationId, areas: $areas) {
      id
      stockLocationId
      platformCityId
      radiusKm
      prepTimeMinutes
      deliverySlaMinutes
      isEnabled
    }
  }
`;

export const SET_STOCK_LOCATION_CARRIERS = gql`
  mutation SetStockLocationCarriers($stockLocationId: ID!, $carriers: [StockLocationCarrierInput!]!) {
    setStockLocationCarriers(stockLocationId: $stockLocationId, carriers: $carriers) {
      id
      carrierCredentialsId
      isEnabled
      sortOrder
    }
  }
`;

export const GET_INVENTORY_HISTORY = gql`
  query GetInventoryHistory($options: InventoryHistoryListOptions) {
    inventoryHistory(options: $options) {
      items {
        id
        doneAt
        createdAt
        state
        pickingType
        quantity
        uomCode
        productVariantId
        productVariantName
        sku
        lotNumber
        expiryDate
        fromLocationName
        toLocationName
        pickingNumber
        reason
        referenceType
        referenceId
        actorUserId
        actorDisplayName
      }
      nextCursor
      hasMore
    }
  }
`;

export const APPLY_STOCK_FILE_ROW = gql`
  mutation ApplyStockFileRow($input: StockFileRowInput!) {
    applyStockFileRow(input: $input) {
      success variantId adjustedCount replayed
      conflicts { code currentStockOnHand }
    }
  }
`;

/**
 * The reasons a tenant may adjust stock with. `manualAdjustInventory` validates the
 * code against this same table, so the picker must be driven by it rather than by a
 * list kept in the dashboard.
 */
export const GET_INVENTORY_REASON_CODES = gql`
  query GetInventoryReasonCodes {
    inventoryReasonCodes {
      id
      code
      name
      direction
    }
  }
`;
